import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from "@remix-run/node"
import {
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useMatches,
} from "@remix-run/react"
import { withSentry } from "@sentry/remix"
import { useEffect } from "react"
import { HoneypotProvider } from "remix-utils/honeypot/react"
import { brandCreator, brandDescription, brandLogoAlt, brandMetaImage, brandName } from "./components/branding.tsx"
import DiscordSupport from "./components/discord-support.tsx"
import { GeneralErrorBoundary } from "./components/error-boundary.tsx"
import Footer from "./components/footer.tsx"
import Header from "./components/header.tsx"
import { EpicProgress } from "./components/progress-bar.tsx"
import { SearchBar } from "./components/search-bar.tsx"
import { useToast } from "./components/toaster.tsx"
import { href as iconsHref } from "./components/ui/icon.tsx"
import { EpicToaster } from "./components/ui/sonner.tsx"
import { ThemeSwitch, useTheme } from "./routes/resources+/theme-switch.tsx"
import tailwindStyleSheetUrl from "./styles/tailwind.css?url"
import { getUserId, logout } from "./utils/auth.server.ts"
import { ClientHintCheck, getHints } from "./utils/client-hints.tsx"
import { prisma } from "./utils/db.server.ts"
import { getEnv } from "./utils/env.server.ts"
import * as gtag from "./utils/gtags.client.ts";
import { honeypot } from "./utils/honeypot.server.ts"
import { combineHeaders, getDomainUrl } from "./utils/misc.tsx"
import { useNonce } from "./utils/nonce-provider.ts"
import { getSelectedServer } from "./utils/selected-server.server.ts"
import { type Theme, getTheme } from "./utils/theme.server.ts"
import { makeTimings, time } from "./utils/timing.server.ts"
import { getToast } from "./utils/toast.server.ts"
import { useOptionalUser } from "./utils/user.ts"

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: "preload", href: iconsHref, as: "image" },
		{
			rel: "alternate icon",
			type: "image/png",
			href: "/favicons/favicon-32x32.png",
		},
		{ rel: "apple-touch-icon", href: "/favicons/apple-touch-icon.png" },
		{
			rel: "manifest",
			href: "/site.webmanifest",
			crossOrigin: "use-credentials",
		} as const, // necessary to make typescript happy
		{ rel: "stylesheet", href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const title = brandName;
	const description = brandDescription;
	return [
		{ title: data ? title : `Error | ${brandName}` },
		{ name: "description", content: description },
		{ property: "og:image", content: brandMetaImage },
		{ property: "og:image:alt", content: brandLogoAlt },
	    { property: "twitter:card", content: "summary_large_image" },
		{ property: "twitter:creator", content: brandCreator },
		{ property: "twitter:site", content: brandCreator },
		{ property: "og:title", content: title },
		{ property: "og:description", content: description },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings("root loader")
	const userId = await time(() => getUserId(request), {
		timings,
		type: "getUserId",
		desc: "getUserId in root",
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: "find user", desc: "find user in root" },
			)
		: null
	if (userId && !user) {
		console.info("something weird happened")
		// something weird happened... The user is authenticated but we can"t find
		// them in the database. Maybe they were deleted? Let"s log them out.
		await logout({ request, redirectTo: "/" })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
					selectedServer: getSelectedServer(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			gaTrackingId: "G-FCW4LYF0WG"
		},
		{
			headers: combineHeaders(
				{ "Server-Timing": timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		"Server-Timing": loaderHeaders.get("Server-Timing") ?? "",
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = "light",
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	const gaTrackingId = "G-FCW4LYF0WG";
	const location = useLocation();
	useEffect(() => {
	  if (gaTrackingId?.length) {
		gtag.pageview(location.pathname, gaTrackingId);
	  }
	}, [location, gaTrackingId]);


	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{!gaTrackingId ? null : (
					<>
						<script
						async
						nonce={nonce}
						src={`https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`}
						/>
						<script
						async
						nonce={nonce}
						id="gtag-init"
						dangerouslySetInnerHTML={{
							__html: `
							window.dataLayer = window.dataLayer || [];
							function gtag(){dataLayer.push(arguments);}
							gtag('js', new Date());
							gtag('config', '${gaTrackingId}', {
							page_path: window.location.pathname,
							});
						`,
						}}
						/>
					</>
				)}
			</head>
			<body className="bg-background text-foreground" id="body-root">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	const isOnSearchPage = matches.find((m) => m.id === "routes/users+/index" ||  m.id === "routes/search+/index")
	const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />
	const allowIndexing = data.ENV.ALLOW_INDEXING !== "false"
	const isOnlyLandingPage = data.ENV.LANDING_PAGE_ONLY === "true"
	useToast(data.toast)

	if (isOnlyLandingPage) {
		var content = tempLandingPage();
		return(
			<Document
				nonce={nonce}
				theme={theme}
				allowIndexing={allowIndexing}
				env={data.ENV}
			>
				<div className="flex h-screen flex-col justify-between">
					<div className="flex-1 text-center center">
						{content}
					</div>
					<div className="container flex justify-between mt-5 pb-5">
						Disclaimer: This is a fan made site and is not directly associated with Ultima Online or UO staff.
						<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
					</div>
				</div>
				<EpicToaster closeButton position="top-center" theme={theme} />
				<EpicProgress />
			</Document>
		)
	}

    return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>

			<div>

			</div>
			
			<div className="flex h-screen flex-col justify-between">
				<Header user={user} searchBar={searchBar} selectedServer={data.requestInfo.userPrefs.selectedServer} />
				<div className="flex-1">
					<Outlet />
				</div>
				<div className="container flex justify-between mt-5 pb-5">
					Disclaimer: This is a fan made site and is not directly associated with Ultima Online or UO staff.
					<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
				</div>
				<Footer user={user} searchBar={searchBar} selectedServer={data.requestInfo.userPrefs.selectedServer} />
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</Document>
	)
}



function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)



export function ErrorBoundary() {
	// the nonce doesn"t rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it"s possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you"ll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}

const tempLandingPage = () => {
	return <main className="font-poppins grid h-full place-items-center p-4">
		<h1 className="mt-8 animate-slide-top text-4xl font-medium text-foreground [animation-delay:0.1s] [animation-fill-mode:backwards] md:text-5xl xl:mt-4 xl:animate-slide-left xl:text-6xl xl:[animation-delay:0.3s] xl:[animation-fill-mode:backwards]">Get Ready to Relive the Adventure</h1>

<div className="flex flex-col md:flex-col-reverse lg:flex-row gap-4 mt-4">
	<div className="m-auto md:ml-auto">
	<img src="/images/skeleton-jase-man.png" className="w-72 p-4 rounded-md" />

</div>
<div className="">
<DiscordSupport />
<p className="mt-6 animate-slide-top text-xl/7 text-muted-foreground [animation-delay:0.8s] [animation-fill-mode:backwards] xl:mt-8 xl:animate-slide-left xl:text-xl/6 xl:leading-10 xl:[animation-delay:1s] xl:[animation-fill-mode:backwards]">
	We are working hard to bring you the best resources for Ultima Online.  Stay tuned for updates.
	<br />
	Scripts, Items, Builds, Monsters, and more!
	<br />- Jaseowns
</p>

	</div>
</div>



	<div className="grid place-items-center px-4 py-8 xl:grid-cols-2 xl:gap-24 border-t-2 mt-4">
		<div className="flex max-w-md flex-col items-center text-center xl:order-2 xl:items-start xl:text-left">
			<Link
				to="/"
				className="animate-slide-top [animation-fill-mode:backwards] xl:animate-slide-left xl:[animation-delay:0.5s] xl:[animation-fill-mode:backwards]"
			>
				<img src={`/images/uoaddicts.png`} />
			</Link>
			<h1
				data-heading
				className="mt-8 animate-slide-top text-4xl font-medium text-foreground [animation-delay:0.3s] [animation-fill-mode:backwards] md:text-5xl xl:mt-4 xl:animate-slide-left xl:text-6xl xl:[animation-delay:0.8s] xl:[animation-fill-mode:backwards]"
			>
				UOAddicts.com
			</h1>
			<p
				data-paragraph
				className="mt-6 animate-slide-top text-xl/7 text-muted-foreground [animation-delay:0.8s] [animation-fill-mode:backwards] xl:mt-8 xl:animate-slide-left xl:text-xl/6 xl:leading-10 xl:[animation-delay:1s] xl:[animation-fill-mode:backwards]"
			>
       			 Whether you're battling dragons, crafting potions, or exploring vast lands, UOAddicts.com will be your ultimate portal to all things Ultima Online.
			</p>
			<p
				data-paragraph
				className="mt-6 animate-slide-top text-xl/7 text-muted-foreground [animation-delay:0.8s] [animation-fill-mode:backwards] xl:mt-8 xl:animate-slide-left xl:text-xl/6 xl:leading-10 xl:[animation-delay:1s] xl:[animation-fill-mode:backwards]"
			>
       			 This site will not be about one specific server, but instead about everything you can experience with Ultima Online in one convenient place.
			</p>

		</div>
		<div className="flex max-w-md flex-col items-center text-center xl:order-2 xl:items-start xl:text-left">
			<Link
				to="/"
				className="animate-slide-top [animation-fill-mode:backwards] xl:animate-slide-left xl:[animation-delay:0.5s] xl:[animation-fill-mode:backwards]"
			>
				<img src={`https://outlands.uorazorscripts.com/uorazorscripts-pvp-pvm.png`} />
			</Link>
			<h1
				data-heading
				className="mt-8 animate-slide-top text-4xl font-medium text-foreground [animation-delay:0.3s] [animation-fill-mode:backwards] md:text-5xl xl:mt-4 xl:animate-slide-left xl:text-6xl xl:[animation-delay:0.8s] xl:[animation-fill-mode:backwards]"
			>
				UORazorScripts.com
			</h1>
			<p
				data-paragraph
				className="mt-6 animate-slide-top text-xl/7 text-muted-foreground [animation-delay:0.8s] [animation-fill-mode:backwards] xl:mt-8 xl:animate-slide-left xl:text-xl/6 xl:leading-10 xl:[animation-delay:1s] xl:[animation-fill-mode:backwards]"
			>
				What started as a website to share scripts with other players, UOAddicts will fill in the gaps with guides, tools, and resources to help you get the most out of your Ultima Online experience.

			</p>

		</div>
	</div>


</main>
}
